import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import AcronisIcon from '@liquidweb-dev/undercurrent/icons/platform/Acronis';
import LoaderCircleIcon from '@liquidweb-dev/undercurrent/icons/animated/LoaderCircle';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Button from '@liquidweb-dev/undercurrent/Button';
import type { DrawerProps } from '@liquidweb-dev/undercurrent/Drawer';
import DrawerHeader from '@liquidweb-dev/undercurrent/DrawerHeader';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import DrawerContent from '@liquidweb-dev/undercurrent/DrawerContent';
import DrawerFooter from '@liquidweb-dev/undercurrent/DrawerFooter';
import MeasureBar from '@liquidweb-dev/undercurrent/MeasureBar';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import TextField from '@liquidweb-dev/undercurrent/TextField';
import { selectors as appConfigSelectors } from 'modules/appConfig';
import { AssetDetails } from 'modules/queries/asset/useDetails';
import useProductFlatDetails from 'modules/queries/product/useFlatDetails';
import useCartItemDetails from 'modules/queries/market/cart/item/useDetails';
import useCartItemAdd from 'modules/queries/market/cart/item/useAdd';
import useCartItemModify from 'modules/queries/market/cart/item/useModify';
import BackupSettingsQuotaSelect from 'containers/pages/servers/BackupsAndImages/BackupsBlock/Settings/QuotaSelect';
import BackupTile from 'containers/pages/servers/BackupsAndImages/BackupsBlock/BackupTile';
import { CART_UUID } from 'utility/constants/baskets';
import acronisProductMap, {
	acronisProductCodes,
} from 'utility/acronisProductMap';
import { useAssetBackupsUpsell } from 'modules/upsell/useUpsellState';
import useProductConfigIntent from 'modules/customerIntent/productConfiguration';

interface UpdateAcronisBackupSettingsProps extends DrawerProps {
	assetDetails: AssetDetails;
	onClose: () => void;
}

const AddAcronisBackupSettings = ({
	assetDetails,
	onClose,
}: UpdateAcronisBackupSettingsProps) => {
	const cartConfig = useSelector(appConfigSelectors.getCartConfig);

	const [destination, setDestination] = useState<string | number>('');
	const [quotaOption, setQuotaOption] = useState<string | number>('');
	const [prevQuotaPtovId, setPrevQuotaPtovId] = useState({});

	const {
		data: acronisProductDetails,
		isLoading: isAcronisProductDetailsLoading,
	} = useProductFlatDetails(
		{
			product_code: acronisProductMap(assetDetails?.type),
		},
		{ enabled: Boolean(assetDetails?.type) },
	);

	const { data: cartData, isLoading: isCartDataLoading } = useCartItemDetails({
		uuid: Cookies.get(CART_UUID),
	});

	const { mutate: addCartItem, isLoading: isAddCartItemLoading } =
		useCartItemAdd();

	const { mutate: modifyCartItem, isLoading: isModifyCartItemLoading } =
		useCartItemModify();

	const acronisDestination = useMemo(
		() =>
			acronisProductDetails?.flat_options?.find(
				(item) => item.key === 'AcronisStorageDestination',
			),
		[acronisProductDetails],
	);

	const [_, setUpsellDismissed] = useAssetBackupsUpsell(assetDetails.uniq_id);

	const destinationOptions = useMemo(
		() =>
			(acronisDestination?.values || [])
				.filter((item) => {
					const destinationOptionValues =
						acronisProductDetails?.flat_options
							?.find((option) => option.parent_ptov_id === item.ptov_id)
							?.values?.filter(
								(value) =>
									value.public &&
									assetDetails?.region_id in (value?.region_id_prices || {}),
							) || [];

					return item.active && destinationOptionValues.length > 0;
				})
				.sort(
					(a, b) =>
						(a.display_order ?? Infinity) - (b.display_order ?? Infinity),
				),
		[acronisDestination, acronisProductDetails, assetDetails?.region_id],
	);

	const quota = useMemo(
		() =>
			acronisProductDetails?.flat_options?.find(
				(item) => item?.parent_ptov_id === destination,
			),
		[acronisProductDetails, destination],
	);

	const quotaOptions = useMemo(
		() =>
			quota?.values
				?.filter((item) => item.public)
				?.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)) || [],
		[quota?.values],
	);

	const existingCartItemUUID = useMemo(
		() =>
			cartData?.items
				?.filter((item) => acronisProductCodes.includes(item.code))
				.find(
					(item) =>
						'properties' in item.details &&
						item.details.properties?.related_subaccnt === assetDetails.uniq_id,
				)?.uuid,
		[assetDetails.uniq_id, cartData],
	);

	useEffect(() => {
		if (destination === '' && destinationOptions.length > 0) {
			setDestination(destinationOptions[0].ptov_id);
		}
	}, [destinationOptions, destination]);

	useEffect(() => {
		if (
			quotaOption === '' &&
			destinationOptions.length > 0 &&
			quotaOptions.length > 0
		) {
			const value = quotaOptions[0].ptov_id;
			setQuotaOption(value);
			const destinationValue = destinationOptions[0].ptov_id;
			setPrevQuotaPtovId((prevState) => ({
				...prevState,
				[destinationValue]: value,
			}));
		}
	}, [destinationOptions, quotaOption, quotaOptions]);

	const productCode = acronisProductMap(assetDetails?.type);
	useProductConfigIntent(productCode);

	const handleSubmit = () => {
		const params = {
			cycle: assetDetails?.cycle,
			item: {
				code: productCode,
				options: [
					{
						parent_ptok_id: null,
						parent_ptov_id: null,
						ptok_id: Number(acronisDestination?.ptok_id),
						ptov_id: destination,
						num_units: null,
					},
					{
						parent_ptok_id: Number(acronisDestination?.ptok_id),
						parent_ptov_id: destination,
						ptok_id: Number(quota?.ptok_id),
						ptov_id: quotaOption,
						num_units: null,
					},
				],
				properties: {
					related_subaccnt: assetDetails.uniq_id,
					domain: assetDetails.domain,
				},
				region_id: assetDetails.region_id,
			},
			uuid: cartData?.uuid || '',
		};

		const onSuccess = () => {
			setUpsellDismissed(true);
			onClose();
			window.location.assign(cartConfig.checkoutURL);
		};

		if (!existingCartItemUUID) {
			addCartItem(params, { onSuccess });
		} else {
			modifyCartItem(
				{ ...params, item_uuid: existingCartItemUUID },
				{ onSuccess },
			);
		}
	};

	const percentage = useMemo(() => {
		const selected = Number(
			quotaOptions?.find((item) => item.ptov_id === quotaOption)?.value || '0',
		);
		const max = Number(quotaOptions[quotaOptions.length - 1]?.value || '1');
		return (selected / max) * 100;
	}, [quotaOption, quotaOptions]);

	const handleDestinationChange = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
	) => {
		const value = event?.target?.value;
		setDestination(Number(value));

		// @ts-ignore
		const prevQuotaValue = prevQuotaPtovId[value];
		if (prevQuotaValue) {
			setQuotaOption(prevQuotaValue);
		} else {
			const targetQuotaOptions = acronisProductDetails?.flat_options
				?.find((item) => item?.parent_ptov_id === value)
				?.values?.filter((item) => item.public)
				?.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
			if (targetQuotaOptions?.[0]?.ptov_id) {
				setQuotaOption(targetQuotaOptions?.[0]?.ptov_id);
			}
		}
	};

	const handleQuotaOptionChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const value = Number(event.target.value);
		setQuotaOption(value);
		setPrevQuotaPtovId((prevState) => ({
			...prevState,
			[destination]: value,
		}));
	};

	return (
		<>
			<DrawerHeader
				onClose={onClose}
				description={
					<Typography
						variant="body-sm"
						sx={({ vars }) => ({ color: vars.palette.uc.text.medium })}
					>
						Configure your backup solution to protect your data
					</Typography>
				}
			>
				Backup settings
			</DrawerHeader>
			<DrawerContent sx={{ gap: 200 }}>
				<BackupTile
					label="Acronis Cyber Backups"
					description="Acronis Cloud delivers ultra-fast, secure, and flexible backup and recovery with military-grade encryption, unmatched speed, and complete control over all your data—anytime, anywhere."
					icon={
						<AcronisIcon
							sx={({ size }) => ({
								height: size.sm,
								width: size.sm,
							})}
						/>
					}
					sx={{ border: 'none', padding: 0 }}
				/>
				<Stack spacing={300} flex={1}>
					{(isAcronisProductDetailsLoading || isCartDataLoading) && (
						<Stack flex={1} justifyContent="center" alignItems="center">
							<LoaderCircleIcon
								sx={({ size }) => ({ height: size.lg, width: size.lg })}
							/>
						</Stack>
					)}
					{!(isAcronisProductDetailsLoading || isCartDataLoading) && (
						<>
							<TextField
								name="destination"
								label="Select backup location"
								value={destination}
								onChange={handleDestinationChange}
								disabled={isAddCartItemLoading || isModifyCartItemLoading}
								fullWidth
								select
							>
								{destinationOptions?.map((item) => (
									<MenuItem key={item.ptov_id} value={item.ptov_id}>
										{item?.description}
									</MenuItem>
								))}
							</TextField>
							<Stack direction="row" spacing={200}>
								<MeasureBar
									orientation="vertical"
									percentage={percentage}
									height={1}
									minWidth={72}
									hidePercentage
								/>
								{acronisProductDetails && (
									<BackupSettingsQuotaSelect
										acronisProductDetails={acronisProductDetails}
										assetDetails={assetDetails}
										destination={destination}
										disabled={isAddCartItemLoading || isModifyCartItemLoading}
										onChange={handleQuotaOptionChange}
										value={quotaOption}
									/>
								)}
							</Stack>
						</>
					)}
				</Stack>
			</DrawerContent>
			<DrawerFooter spacing={100}>
				<Button
					variant="primary"
					color="highlight"
					onClick={handleSubmit}
					loading={isAddCartItemLoading || isModifyCartItemLoading}
					disabled={isAcronisProductDetailsLoading || isCartDataLoading}
					size="large"
				>
					Buy now
				</Button>
				<Button
					variant="ghost"
					onClick={onClose}
					disabled={isAddCartItemLoading || isModifyCartItemLoading}
					size="large"
				>
					Cancel
				</Button>
			</DrawerFooter>
		</>
	);
};

export default AddAcronisBackupSettings;
